import {
  Anchor,
  Box,
  Button,
  Divider,
  Flex,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { FaRegCalendarDays } from 'react-icons/fa6';
import type { UpcomingJob } from '@th-types/upcoming-job.type';

export interface UpcomingJobsWidgetProps {
  jobs: UpcomingJob[];
  jobBaseUrl: string;
  publicJobsUrl: string;
}

function JobRow({ job, jobBaseUrl }: { job: UpcomingJob; jobBaseUrl: string }) {
  const href = `${jobBaseUrl}?jobAssignmentId=${job.jobAssignmentId}`;

  return (
    <Anchor
      href={href}
      underline="never"
      c="inherit"
      display="block"
      style={{ cursor: 'pointer' }}
    >
      <Flex gap="md" wrap="nowrap" align="center" py={6}>
        <Box w={48} style={{ textAlign: 'center', flexShrink: 0 }}>
          <Text fw={700} fz={24} lh={1.1}>
            {job.startDay}
          </Text>
          <Text fz={15} c="dimmed" lh={1.2}>
            {job.startMonth}
          </Text>
        </Box>
        <Divider orientation="vertical" color="#F4F4F4" size={2} />
        <Stack gap={0}>
          <Text fw={600} fz={14} lh={1.3}>
            {job.eventName}
          </Text>
          <Text fz={13} c="dimmed" lh={1.3}>
            {job.brandRepresented}
          </Text>
        </Stack>
      </Flex>
    </Anchor>
  );
}

function EmptyState({ publicJobsUrl }: { publicJobsUrl: string }) {
  return (
    <Stack align="center" gap="sm" py="lg" maw={300} mx="auto" ta="center">
      <Box
        style={{
          width: 105,
          height: 105,
          borderRadius: '50%',
          background: '#f9f0cc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <FaRegCalendarDays size={50} color="#e2bd2e" />
      </Box>
      <Text fz={13} fw={700} ta="center" c="#666" mt={10}>
        Looks like you don&apos;t have any upcoming jobs. Click below to see
        what&apos;s available!
      </Text>
      <Button
        component="a"
        href={publicJobsUrl}
        variant="outline"
        color="#9B9B9B"
        radius={4}
        mt={2}
      >
        View Public Jobs
      </Button>
    </Stack>
  );
}

export function UpcomingJobsWidget({
  jobs,
  jobBaseUrl,
  publicJobsUrl,
}: UpcomingJobsWidgetProps) {
  return (
    <Paper shadow="var(--widget-shadow)" px={10} pb={20}>
      <Text
        fw={700}
        fz={15}
        ta="center"
        lh="50px"
        style={{ borderBottom: '1px solid #e3e3e3' }}
      >
        My Upcoming Jobs
      </Text>
      {jobs.length > 0 ? (
        <Stack gap={0}>
          {jobs.map((job) => (
            <JobRow
              key={job.jobAssignmentId}
              job={job}
              jobBaseUrl={jobBaseUrl}
            />
          ))}
        </Stack>
      ) : (
        <EmptyState publicJobsUrl={publicJobsUrl} />
      )}
    </Paper>
  );
}

export default UpcomingJobsWidget;
